/**
 * Auth Store - Manages authentication state and API key management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

export interface TenantInfo {
  id: string;
  name: string;
  email: string;
  plan: 'starter' | 'growth' | 'business' | 'enterprise';
  usage?: {
    verifications: number;
    credentials: number;
    periodStart: string;
    periodEnd: string;
  };
}

export interface AuthState {
  // Auth state
  isAuthenticated: boolean;
  apiKey: string | null;
  apiKeyPreview: string | null;
  tenantId: string | null;
  tenantName: string | null;
  tenant: TenantInfo | null;
  authMode: 'api-key' | 'session' | null;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (apiKey: string) => Promise<boolean>;
  loginWithSession: (apiKey: string) => Promise<boolean>;
  logout: () => void;
  validateSession: () => Promise<boolean>;
  refreshTenantInfo: () => Promise<void>;
  clearError: () => void;
}

// Plan limits for UI display
export const PLAN_LIMITS = {
  starter: { verifications: 1000, rateLimit: 100 },
  growth: { verifications: 10000, rateLimit: 500 },
  business: { verifications: 100000, rateLimit: 2000 },
  enterprise: { verifications: Infinity, rateLimit: 10000 },
};

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      apiKey: null,
      apiKeyPreview: null,
      tenantId: null,
      tenantName: null,
      tenant: null,
      authMode: null,
      isLoading: false,
      error: null,

      // Login with API key
      login: async (apiKey: string) => {
        set({ isLoading: true, error: null });

        try {
          // Set the API key in the client
          api.setApiKey(apiKey);

          // Validate by making an API call
          const response = await api.health();

          if (response.error) {
            api.setApiKey(null);
            set({
              isLoading: false,
              error: 'Invalid API key or server error',
              isAuthenticated: false,
            });
            return false;
          }
          
          // Create a preview of the API key (first 10 + last 4 chars)
          const keyPreview = apiKey.length > 16 
            ? `${apiKey.slice(0, 10)}...${apiKey.slice(-4)}`
            : apiKey.slice(0, 8) + '...';

          // Set authenticated state
          set({
            isAuthenticated: true,
            apiKey,
            apiKeyPreview: keyPreview,
            authMode: 'api-key',
            isLoading: false,
            error: null,
          });

          // Fetch tenant info in background
          get().refreshTenantInfo();

          return true;
        } catch (error) {
          api.setApiKey(null);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
            isAuthenticated: false,
          });
          return false;
        }
      },

      // Login with session (httpOnly cookie - more secure)
      // API key is exchanged for a session cookie and NOT stored in localStorage
      loginWithSession: async (apiKey: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.sessionLogin(apiKey);

          if (response.error || !response.data?.success) {
            set({
              isLoading: false,
              error: response.error || 'Session login failed',
              isAuthenticated: false,
            });
            return false;
          }

          set({
            isAuthenticated: true,
            apiKey: null, // NOT stored — using session cookie
            apiKeyPreview: 'session',
            tenantId: response.data.tenantId,
            tenantName: response.data.tenantName,
            authMode: 'session',
            isLoading: false,
            error: null,
          });

          // Fetch full tenant info
          get().refreshTenantInfo();

          return true;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Session login failed',
            isAuthenticated: false,
          });
          return false;
        }
      },

      // Refresh tenant information from API
      refreshTenantInfo: async () => {
        try {
          const meResponse = await api.me();
          
          if (!meResponse.error && meResponse.data) {
            const data = meResponse.data;
            set({
              tenantId: data.id,
              tenantName: data.name,
              tenant: {
                id: data.id,
                name: data.name,
                email: data.email,
                plan: data.plan,
                usage: {
                  verifications: data.usage.verifications,
                  credentials: data.usage.credentials,
                  periodStart: data.usage.periodStart,
                  periodEnd: data.usage.periodEnd,
                },
              },
              apiKeyPreview: data.apiKey.preview,
            });
          }
        } catch (error) {
          console.error('Failed to refresh tenant info:', error);
        }
      },

      // Logout
      logout: () => {
        // Destroy session cookie if using session auth
        if (get().authMode === 'session') {
          api.sessionLogout().catch(() => {});
        }
        api.setApiKey(null);
        set({
          isAuthenticated: false,
          apiKey: null,
          apiKeyPreview: null,
          tenantId: null,
          tenantName: null,
          tenant: null,
          authMode: null,
          error: null,
        });
      },

      // Validate existing session (API key or cookie)
      validateSession: async () => {
        const { apiKey, authMode } = get();

        // If using session auth, check cookie validity
        if (authMode === 'session') {
          set({ isLoading: true });
          try {
            const response = await api.sessionCheck();
            if (response.data?.authenticated) {
              set({ isLoading: false, isAuthenticated: true });
              get().refreshTenantInfo();
              return true;
            }
          } catch {
            // Session invalid
          }
          set({
            isAuthenticated: false,
            authMode: null,
            tenantId: null,
            tenant: null,
            isLoading: false,
          });
          return false;
        }

        // Legacy API key validation
        if (!apiKey) {
          return false;
        }

        set({ isLoading: true });

        try {
          api.setApiKey(apiKey);
          const response = await api.health();

          if (response.error) {
            // Session is invalid
            set({
              isAuthenticated: false,
              apiKey: null,
              tenantId: null,
              tenant: null,
              isLoading: false,
            });
            return false;
          }

          set({ isLoading: false, isAuthenticated: true });
          
          // Refresh tenant info
          get().refreshTenantInfo();
          
          return true;
        } catch {
          set({
            isAuthenticated: false,
            apiKey: null,
            tenantId: null,
            tenant: null,
            isLoading: false,
          });
          return false;
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'zkcn-auth',
      // Persist fields needed for session recovery on page reload
      partialize: (state) => ({
        apiKey: state.apiKey,
        apiKeyPreview: state.apiKeyPreview,
        tenantId: state.tenantId,
        tenantName: state.tenantName,
        tenant: state.tenant,
        authMode: state.authMode,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
